
import {
  defineComponent, onBeforeMount, reactive, onMounted, onBeforeUnmount,
} from 'vue';
import { useRoute } from 'vue-router';
import { useAppBase } from '@/core/composables/AppBase';
import { IPedido, IPedidoHistorico } from '@/models/Entidades/IPedido';
import ComunicacaoApi from '@/core/components/UI/ComunicacaoApi.vue';
import ServicoPedido from '@/servicos/ServicoPedido';
import { IResposta } from '@/core/models/IResposta';
import { EStatusPedido } from '@/models/Enumeradores/EStatusPedido';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import UtilitarioMascara from '@/core/utilitarios/UtilitarioMascara';
import UtilitarioData from '@/core/utilitarios/UtilitarioData';
import ComboStatusPedido from '@/components/Pedidos/ComboStatusPedido.vue';
import { EStatusResposta } from '@/models/Enumeradores/EStatusResposta';
import { IDTOPacote } from '@/models/DTO/IDTOPacote';

export default defineComponent({
  name: 'PedidoCadastro',
  components: {
    ComunicacaoApi,
    ComboStatusPedido,
  },
  setup() {
    const {
      appBase, apresentarComunicacaoApi, apresentarResposta,
    } = useAppBase();

    const route = useRoute();
    const servicoPedido = new ServicoPedido();

    const state = reactive({
      pedido: {} as IPedido,
      apresentarModalStatus: false,
      formasEntregasSelecionadas: [] as number[],
      historico: {} as IPedidoHistorico,
      pacotes: [] as IDTOPacote[],
    });

    const tituloPaginaOriginal = document.title;

    // Título sugerido para a impressão
    let tituloImpressao = '';

    async function obterPedido(numeroIdentificacao:string) {
      appBase.carregando = true;
      state.pedido = await servicoPedido.obterPorNumeroIdentificacao(numeroIdentificacao);
      tituloImpressao = `Pedido ${state.pedido.numeroIdentificacao} - ${state.pedido.nomeRazaoSocial}`;
      state.pacotes = await servicoPedido.obterPacotes(state.pedido);
      appBase.carregando = false;
    }

    function limparDados() {
      state.formasEntregasSelecionadas = [];
      state.pedido = {} as IPedido;
      state.pedido.codigo = 0;
      state.historico = {} as IPedidoHistorico;
      state.historico.codigo = 0;
      state.historico.codigoPedido = 0;
    }

    onBeforeMount(async () => {
      limparDados();

      if (route.params !== undefined && UtilitarioGeral.valorValido(route.params.numeroIdentificacao.toString())) {
        await obterPedido(route.params.numeroIdentificacao.toString());
      }
    });

    function handleBeforePrint() {
      // Altera temporariamente o título do documento para o nome desejado
      document.title = tituloImpressao;
    }

    function handleAfterPrint() {
      // Restaura o título original após a impressão
      document.title = tituloPaginaOriginal;
    }

    onMounted(() => {
      window.addEventListener('beforeprint', handleBeforePrint);
      window.addEventListener('afterprint', handleAfterPrint);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('beforeprint', handleBeforePrint);
      window.removeEventListener('afterprint', handleAfterPrint);
    });

    async function atualizarStatusPedido() {
      appBase.resposta = {} as IResposta;
      apresentarComunicacaoApi('Aguarde por favor... Estamos salvando as informações do Pedido.');
      state.historico.codigoPedido = state.pedido.codigo;
      appBase.resposta = await servicoPedido.atualizarStatusHistorico(state.historico);
      if (appBase.resposta.status === EStatusResposta.Sucesso) {
        state.pedido.status = state.historico.status;
        state.historico = {} as IPedidoHistorico;
        state.historico.codigo = 0;
        state.historico.detalhes = '';
        state.apresentarModalStatus = false;
      }
      apresentarResposta();
    }

    function obterDescricaoStatus(status:number):string {
      return servicoPedido.obterDescricaoStatus(status);
    }

    function obterLabelCpfCnpj():string {
      return state.pedido.cpfCnpj.length === 11 ? 'CPF' : 'CNPJ';
    }

    function totalProdutos():number {
      let valorTotalProdutos = 0;
      if (UtilitarioGeral.validaLista(state.pedido.itens)) {
        state.pedido.itens.forEach((item) => {
          valorTotalProdutos += item.valorTotal;
        });
      }
      return valorTotalProdutos;
    }

    function apresentarEnderecoEntrega(): boolean {
      if (UtilitarioGeral.objetoValido(state.pedido.entrega)) {
        if (UtilitarioGeral.validaCodigo(state.pedido.entrega.codigo) && UtilitarioGeral.valorValido(state.pedido.entrega.cep)) {
          return true;
        }
      }
      return false;
    }

    function apresentacaoEndereco(): string {
      let enderecoCompleto = '';
      if (UtilitarioGeral.valorValido(state.pedido.entrega.cep)) {
        enderecoCompleto += ` CEP: ${state.pedido.entrega.cep}`;
      }

      if (UtilitarioGeral.valorValido(state.pedido.entrega.endereco)) {
        enderecoCompleto += `, ${state.pedido.entrega.endereco}`;
      }

      if (UtilitarioGeral.valorValido(state.pedido.entrega.numero)) {
        enderecoCompleto += `, Nº ${state.pedido.entrega.numero}`;
      }

      if (UtilitarioGeral.valorValido(state.pedido.entrega.complemento)) {
        enderecoCompleto += `, ${state.pedido.entrega.complemento}`;
      }

      if (UtilitarioGeral.valorValido(state.pedido.entrega.bairro)) {
        enderecoCompleto += `, ${state.pedido.entrega.bairro}`;
      }

      if (UtilitarioGeral.valorValido(state.pedido.entrega.cidade)) {
        enderecoCompleto += `, ${state.pedido.entrega.cidade}-${state.pedido.entrega.estado}`;
      }

      return enderecoCompleto;
    }

    function apresentacaoPacote(pacote:IDTOPacote): string {
      let apresentacaoPacote = '';
      if (pacote.pesoTotal > 0) {
        apresentacaoPacote += ` <b>Peso Total:</b> ${pacote.pesoTotal} Kg`;
      }

      if (pacote.altura > 0) {
        apresentacaoPacote += ` <b>Altura:</b> ${pacote.altura} cm`;
      }

      if (pacote.largura > 0) {
        apresentacaoPacote += ` <b>Largura:</b> ${pacote.largura} cm`;
      }

      if (pacote.comprimento > 0) {
        apresentacaoPacote += ` <b>Comprimento:</b> ${pacote.comprimento} cm`;
      }

      return apresentacaoPacote;
    }

    return {
      appBase,
      state,
      EStatusPedido,
      UtilitarioGeral,
      UtilitarioMascara,
      UtilitarioData,
      obterDescricaoStatus,
      obterLabelCpfCnpj,
      totalProdutos,
      atualizarStatusPedido,
      apresentarEnderecoEntrega,
      apresentacaoEndereco,
      apresentacaoPacote,
    };
  },
});
